import React, { useState, useEffect } from 'react';
import axiosClient from '../api/axiosClient';
import { generatePassword } from '../utils/passwordGenerator';

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Modal states
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [resetDone, setResetDone] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deactivateReason, setDeactivateReason] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  // Create user form state
  const [companies, setCompanies] = useState([]);
  const [createFormData, setCreateFormData] = useState({
    company_id: '',
    email: '',
    username: '',
    password: '',
    full_name: '',
    role: 'client'
  });

  useEffect(() => {
    fetchAllUsers();
    fetchCompanies();
  }, []);

  const fetchAllUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Fetch all users from database by querying companies and their users
      // For now, we'll use a simple approach - get cert bodies and clients
      const [certBodiesRes, clientsRes] = await Promise.all([
        axiosClient.get('/cert-bodies/', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axiosClient.get('/clients/', {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      // Collect all users from cert bodies and clients
      const allUsers = [];

      // Add cert body admins
      for (const certBody of certBodiesRes.data) {
        if (certBody.users) {
          certBody.users.forEach(user => {
            allUsers.push({
              ...user,
              company_name: certBody.company_name,
              company_type: 'Certification Body'
            });
          });
        }
      }

      // Add client users
      for (const client of clientsRes.data) {
        // Fetch detailed client info to get users
        const clientDetail = await axiosClient.get(
          `/clients/${client.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (clientDetail.data.users) {
          clientDetail.data.users.forEach(user => {
            allUsers.push({
              ...user,
              company_name: clientDetail.data.company.name,
              company_type: 'Client Company'
            });
          });
        }
      }

      setUsers(allUsers);
      setError('');
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('token');

      const [certBodiesRes, clientsRes] = await Promise.all([
        axiosClient.get('/cert-bodies/', { headers: { Authorization: `Bearer ${token}` } }),
        axiosClient.get('/clients/', { headers: { Authorization: `Bearer ${token}` } })
      ]);

      const allCompanies = [
        ...certBodiesRes.data.map(cb => ({ id: cb.id, name: cb.company_name || cb.name, type: 'Certification Body' })),
        ...clientsRes.data.map(c => ({ id: c.id, name: c.name, type: 'Client Company' }))
      ];

      setCompanies(allCompanies);
    } catch (err) {
      console.error('Error fetching companies:', err);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');
      const company = companies.find(c => c.id === parseInt(createFormData.company_id));

      if (!company) {
        setError('Please select a company');
        setTimeout(() => setError(''), 3000);
        return;
      }

      // Determine the API endpoint based on company type
      let endpoint;
      let payload;

      if (company.type === 'Certification Body') {
        endpoint = `/cert-bodies/${company.id}/users`;
        payload = {
          email: createFormData.email,
          username: createFormData.username,
          password: createFormData.password,
          full_name: createFormData.full_name,
          is_active: true
        };
      } else {
        endpoint = `/clients/${company.id}/users`;
        payload = {
          email: createFormData.email,
          username: createFormData.username,
          password: createFormData.password,
          full_name: createFormData.full_name || createFormData.username,
          is_active: true
        };
      }

      await axiosClient.post(endpoint, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccessMessage(`User ${createFormData.username} created successfully`);
      setShowCreateModal(false);
      setCreateFormData({
        company_id: '',
        email: '',
        username: '',
        password: '',
        full_name: '',
        role: 'client'
      });
      fetchAllUsers();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create user');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleDeactivate = (user) => {
    setSelectedUser(user);
    setDeactivateReason('');
    setShowDeactivateModal(true);
  };

  const confirmDeactivate = async () => {
    try {
      const token = localStorage.getItem('token');
      await axiosClient.post(
        `/users/${selectedUser.id}/deactivate`,
        { reason: deactivateReason || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccessMessage(`User ${selectedUser.username} deactivated successfully`);
      setShowDeactivateModal(false);
      fetchAllUsers();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to deactivate user');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleReactivate = async (user) => {
    try {
      const token = localStorage.getItem('token');
      await axiosClient.post(
        `/users/${user.id}/reactivate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccessMessage(`User ${user.username} reactivated successfully`);
      fetchAllUsers();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reactivate user');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleExportData = async (user) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axiosClient.get(
        `/users/${user.id}/export-data`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `user_data_${user.username}_${user.id}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setSuccessMessage(`Data exported for ${user.username}`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to export user data');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDelete = (user) => {
    setSelectedUser(user);
    setDeleteConfirmation('');
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (deleteConfirmation !== 'DELETE_MY_ACCOUNT') {
      setError('Please type DELETE_MY_ACCOUNT to confirm');
      setTimeout(() => setError(''), 3000);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axiosClient.delete(
        `/users/${selectedUser.id}/delete-account?confirm=DELETE_MY_ACCOUNT`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccessMessage(`User ${selectedUser.username} permanently deleted`);
      setShowDeleteModal(false);
      fetchAllUsers();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete user');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleOpenReset = (user) => {
    setSelectedUser(user);
    setResetPassword(generatePassword());
    setResetDone(false);
    setShowResetModal(true);
  };

  const confirmResetPassword = async () => {
    try {
      const token = localStorage.getItem('token');
      await axiosClient.post(
        `/users/${selectedUser.id}/reset-password`,
        { new_password: resetPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResetDone(true);
      setSuccessMessage(`Password reset for ${selectedUser.username}`);
      setTimeout(() => setSuccessMessage(''), 4000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
      setTimeout(() => setError(''), 4000);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super_admin': return '#dc3545';
      case 'third_party_admin': return '#ffc107';
      case 'client': return '#28a745';
      default: return '#6c757d';
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
        <div>
          <h1 style={{ marginBottom: '10px', color: '#333' }}>User Management</h1>
          <p style={{ color: '#666', marginBottom: 0 }}>
            Manage user accounts, deactivate/reactivate users, and handle GDPR data requests
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          style={{
            padding: '10px 20px',
            background: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          + Create User
        </button>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div style={{
          background: '#d4edda',
          color: '#155724',
          padding: '15px',
          borderRadius: '4px',
          marginBottom: '20px',
          border: '1px solid #c3e6cb'
        }}>
          {successMessage}
        </div>
      )}

      {error && (
        <div style={{
          background: '#f8d7da',
          color: '#721c24',
          padding: '15px',
          borderRadius: '4px',
          marginBottom: '20px',
          border: '1px solid #f5c6cb'
        }}>
          {error}
        </div>
      )}

      {/* Users Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          Loading users...
        </div>
      ) : users.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          No users found
        </div>
      ) : (
        <div style={{
          background: 'white',
          borderRadius: '8px',
          overflow: 'hidden',
          border: '1px solid #dee2e6'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#343a40' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#ffffff' }}>
                  User
                </th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#ffffff' }}>
                  Company
                </th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#ffffff' }}>
                  Role
                </th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#ffffff' }}>
                  Status
                </th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#ffffff' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '12px' }}>
                    <div style={{ fontWeight: '500', fontSize: '14px', color: '#212529' }}>
                      {user.full_name || user.username}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6c757d' }}>
                      {user.email}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6c757d' }}>
                      @{user.username}
                    </div>
                  </td>
                  <td style={{ padding: '12px', fontSize: '13px', color: '#495057' }}>
                    <div>{user.company_name}</div>
                    <div style={{ fontSize: '12px', color: '#6c757d' }}>{user.company_type}</div>
                  </td>
                  <td style={{ padding: '12px' }}>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      background: getRoleBadgeColor(user.role) + '20',
                      color: getRoleBadgeColor(user.role),
                      textTransform: 'capitalize'
                    }}>
                      {user.role.replace('_', ' ')}
                    </span>
                  </td>
                  <td style={{ padding: '12px' }}>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      background: user.is_active ? '#d4edda' : '#f8d7da',
                      color: user.is_active ? '#155724' : '#721c24'
                    }}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {user.is_active ? (
                        <button
                          onClick={() => handleDeactivate(user)}
                          style={{
                            padding: '6px 12px',
                            background: '#ffc107',
                            color: '#000',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Deactivate
                        </button>
                      ) : (
                        <button
                          onClick={() => handleReactivate(user)}
                          style={{
                            padding: '6px 12px',
                            background: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Reactivate
                        </button>
                      )}
                      
                      <button
                        onClick={() => handleExportData(user)}
                        style={{
                          padding: '6px 12px',
                          background: '#17a2b8',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                        title="Export user data (GDPR)"
                      >
                        Export Data
                      </button>
                      
                      <button
                        onClick={() => handleOpenReset(user)}
                        style={{
                          padding: '6px 12px',
                          background: '#6f42c1',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                        title="Reset password"
                      >
                        Reset Password
                      </button>

                      <button
                        onClick={() => handleDelete(user)}
                        style={{
                          padding: '6px 12px',
                          background: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                        title="Permanently delete account (GDPR)"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Deactivate Modal */}
      {showDeactivateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h2 style={{ marginTop: 0, color: '#333' }}>Deactivate User</h2>
            <p style={{ color: '#666' }}>
              Deactivate user: <strong>{selectedUser?.username}</strong> ({selectedUser?.email})
            </p>
            <p style={{ color: '#666', fontSize: '14px' }}>
              The user will not be able to log in, but their data will be preserved.
            </p>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#495057' }}>
                Reason (optional)
              </label>
              <textarea
                value={deactivateReason}
                onChange={(e) => setDeactivateReason(e.target.value)}
                placeholder="e.g., User requested account suspension"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '14px',
                  minHeight: '80px'
                }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowDeactivateModal(false)}
                style={{
                  padding: '10px 20px',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeactivate}
                style={{
                  padding: '10px 20px',
                  background: '#ffc107',
                  color: '#000',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Deactivate User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h2 style={{ marginTop: 0, color: '#dc3545' }}> Permanently Delete User</h2>
            <p style={{ color: '#666' }}>
              You are about to permanently delete: <strong>{selectedUser?.username}</strong> ({selectedUser?.email})
            </p>
            <div style={{
              background: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '4px',
              padding: '15px',
              marginBottom: '20px'
            }}>
              <strong style={{ color: '#856404' }}>Warning:</strong>
              <ul style={{ margin: '10px 0 0 20px', color: '#856404' }}>
                <li>This action cannot be undone</li>
                <li>All user data will be permanently deleted</li>
                <li>This complies with GDPR Article 17 (Right to be Forgotten)</li>
              </ul>
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#495057' }}>
                Type <code style={{ background: '#f8f9fa', padding: '2px 6px', borderRadius: '3px' }}>DELETE_MY_ACCOUNT</code> to confirm
              </label>
              <input
                type="text"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder="DELETE_MY_ACCOUNT"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowDeleteModal(false)}
                style={{
                  padding: '10px 20px',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteConfirmation !== 'DELETE_MY_ACCOUNT'}
                style={{
                  padding: '10px 20px',
                  background: deleteConfirmation === 'DELETE_MY_ACCOUNT' ? '#dc3545' : '#e9ecef',
                  color: deleteConfirmation === 'DELETE_MY_ACCOUNT' ? 'white' : '#6c757d',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: deleteConfirmation === 'DELETE_MY_ACCOUNT' ? 'pointer' : 'not-allowed',
                  fontWeight: '500'
                }}
              >
                Permanently Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: 'white', padding: '30px', borderRadius: '8px',
            maxWidth: '480px', width: '90%'
          }}>
            <h2 style={{ marginTop: 0, color: '#6f42c1' }}>Reset Password</h2>
            <p style={{ color: '#666' }}>
              User: <strong>{selectedUser?.full_name || selectedUser?.username}</strong> ({selectedUser?.email})
            </p>

            {!resetDone ? (
              <>
                <p style={{ color: '#555', fontSize: '14px' }}>
                  A new secure password has been generated. Click <strong>Apply Reset</strong> to set it,
                  then share it securely with the user.
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
                  <input
                    type="text"
                    value={resetPassword}
                    onChange={e => setResetPassword(e.target.value)}
                    style={{
                      flex: 1, padding: '10px', border: '1px solid #ced4da',
                      borderRadius: '4px', fontSize: '15px', fontFamily: 'monospace'
                    }}
                  />
                  <button
                    onClick={() => setResetPassword(generatePassword())}
                    style={{
                      padding: '10px 14px', background: '#e9ecef', border: '1px solid #ced4da',
                      borderRadius: '4px', cursor: 'pointer', fontSize: '13px', whiteSpace: 'nowrap'
                    }}
                  >
                    Regenerate
                  </button>
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button onClick={() => setShowResetModal(false)}
                    style={{ padding: '10px 20px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                    Cancel
                  </button>
                  <button onClick={confirmResetPassword}
                    style={{ padding: '10px 20px', background: '#6f42c1', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '500' }}>
                    Apply Reset
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{ background: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '4px', padding: '15px', marginBottom: '20px' }}>
                  <strong style={{ color: '#155724' }}>Password reset successfully!</strong>
                  <p style={{ margin: '8px 0 0', color: '#155724', fontSize: '14px' }}>New password:</p>
                  <code style={{ display: 'block', marginTop: '6px', fontSize: '16px', letterSpacing: '1px', color: '#333' }}>
                    {resetPassword}
                  </code>
                </div>
                <p style={{ color: '#856404', fontSize: '13px', background: '#fff3cd', padding: '10px', borderRadius: '4px' }}>
                  Copy this password and share it securely with the user. It will not be shown again.
                </p>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => navigator.clipboard.writeText(resetPassword)}
                    style={{ padding: '10px 20px', background: '#17a2b8', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                    Copy Password
                  </button>
                  <button onClick={() => setShowResetModal(false)}
                    style={{ padding: '10px 20px', background: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '500' }}>
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{ marginTop: 0, color: '#333' }}>Create New User</h2>
            <form onSubmit={handleCreateUser}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#495057', fontWeight: '500' }}>
                  Company <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <select
                  value={createFormData.company_id}
                  onChange={(e) => setCreateFormData({ ...createFormData, company_id: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Select a company...</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>
                      {company.name} ({company.type})
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#495057', fontWeight: '500' }}>
                  Email <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <input
                  type="email"
                  value={createFormData.email}
                  onChange={(e) => setCreateFormData({ ...createFormData, email: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  placeholder="user@example.com"
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#495057', fontWeight: '500' }}>
                  Username <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <input
                  type="text"
                  value={createFormData.username}
                  onChange={(e) => setCreateFormData({ ...createFormData, username: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  placeholder="username"
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#495057', fontWeight: '500' }}>
                  Full Name
                </label>
                <input
                  type="text"
                  value={createFormData.full_name}
                  onChange={(e) => setCreateFormData({ ...createFormData, full_name: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  placeholder="John Doe"
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#495057', fontWeight: '500' }}>
                  Password <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <input
                  type="password"
                  value={createFormData.password}
                  onChange={(e) => setCreateFormData({ ...createFormData, password: e.target.value })}
                  required
                  minLength="8"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  placeholder="Minimum 8 characters"
                />
                <small style={{ color: '#6c757d', fontSize: '12px' }}>
                  Password must be at least 8 characters long
                </small>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setCreateFormData({
                      company_id: '',
                      email: '',
                      username: '',
                      password: '',
                      full_name: '',
                      role: 'client'
                    });
                  }}
                  style={{
                    padding: '10px 20px',
                    background: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    background: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
